import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { selectBoxDetailsForm, selectWorkOrderVehicleDetails } from "../../../../redux/selector";
import { actions } from "../../../../redux/slice";
import { BOX_REAR_WALL_TYPE_SELECT_BY_WALL, BoxDetailsFormValues } from "../../../../types";
import {
  BOX_DETAILS_FORM_I18N_PREFIX,
  BOX_REAR_WALL_TYPE_FIELD,
  getMockVehicleDetails,
} from "../../data";
import { FormFieldConfig } from "../../types";
import { FormFieldControl } from "../../VehicleDetails/FormControls";
import { useStyles } from "../styles";

const stepNumber = (value: string, delta: number) =>
  String(Math.max(0, (Number(value) || 0) + delta));

const NumberStepperField = ({
  labelKey,
  value,
  onChange,
  narrow,
  readOnly,
}: {
  labelKey: string;
  value: string;
  onChange: (value: string) => void;
  narrow: boolean;
  readOnly: boolean;
}) => {
  const { t } = useTranslation();
  const styles = useStyles();

  return (
    <Box className={narrow ? styles.stepperFieldNarrow : styles.stepperField}>
      <Typography className={styles.stepperLabel}>{t(labelKey)}</Typography>
      <Box className={narrow ? styles.stepperBoxNarrow : styles.stepperBox}>
        <Box className={styles.stepperArrows}>
          <KeyboardArrowUpIcon
            className={styles.stepperArrow}
            onClick={() => onChange(stepNumber(value, 1))}
          />
          <KeyboardArrowDownIcon
            className={styles.stepperArrow}
            onClick={() => onChange(stepNumber(value, -1))}
          />
        </Box>
        <input
          className={styles.stepperValue}
          value={value}
          readOnly={readOnly}
          onChange={(event) => onChange(event.target.value)}
        />
      </Box>
    </Box>
  );
};

export const renderFieldControls = <T extends Record<string, string | boolean>>(
  fields: FormFieldConfig<T>[],
  formValues: T,
  updateField: (field: keyof T, value: string | boolean) => void
) =>
  fields.map((config) =>
    config.type === "number" && config.showSpinners ? (
      <NumberStepperField
        key={String(config.field)}
        labelKey={config.labelKey}
        value={formValues[config.field] as string}
        narrow={Boolean(config.narrow)}
        readOnly={Boolean(config.readOnly)}
        onChange={(value) => updateField(config.field, value)}
      />
    ) : (
      <FormFieldControl
        key={String(config.field)}
        config={config}
        value={formValues[config.field] as string}
        onChange={(value) => updateField(config.field, value)}
      />
    )
  );

export const useBoxDetails = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const formValues = useSelector(selectBoxDetailsForm);
  const vehicleDetails = useSelector(selectWorkOrderVehicleDetails);
  const boxType = (
    vehicleDetails ?? getMockVehicleDetails(Number(id))
  ).boxType;
  const usesCompactRoofRow =
    boxType === "CURTAIN_ROOF" ||
    boxType === "CLOSED_BOX" ||
    boxType === "REFRIGERATED";
  const formPrefix = BOX_DETAILS_FORM_I18N_PREFIX;

  const updateField = (
    field: keyof BoxDetailsFormValues,
    value: string | boolean
  ) => {
    dispatch(actions.updateBoxDetailsFormField({ field, value }));
    if (field === "rearWall" && typeof value === "string") {
      const types = BOX_REAR_WALL_TYPE_SELECT_BY_WALL[value];
      dispatch(
        actions.updateBoxDetailsFormField({
          field: "rearWallType",
          value: types ? types[0][0] : "",
        })
      );
    }
  };

  const rearWallTypeOptions = formValues
    ? BOX_REAR_WALL_TYPE_SELECT_BY_WALL[formValues.rearWall] || []
    : [];
  const rearWallTypeConfig = rearWallTypeOptions.length
    ? { ...BOX_REAR_WALL_TYPE_FIELD, type: "select" as const, options: rearWallTypeOptions }
    : BOX_REAR_WALL_TYPE_FIELD;

  return {
    formValues,
    formPrefix,
    updateField,
    usesCompactRoofRow,
    rearWallTypeConfig,
  };
};
